import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config/api';

export default function DraftQueuePanel({ isOpen, onClose, currentUser, onStartOrder }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/draft-orders`);
      if (!res.ok) throw new Error('Failed to load drafts');
      const data = await res.json();
      setDrafts(data);
    } catch (err) {
      console.error('DraftQueuePanel fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchDrafts();
  }, [isOpen, fetchDrafts]);

  const claimDraft = async (id) => {
    try {
      await fetch(`${API_URL}/api/draft-orders/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'claimed', claimedBy: currentUser }),
      });
      fetchDrafts();
    } catch (err) {
      console.error('Claim draft error:', err);
    }
  };

  const deleteDraft = async (id) => {
    if (!window.confirm('Remove this draft from the queue?')) return;
    try {
      await fetch(`${API_URL}/api/draft-orders/${id}`, { method: 'DELETE' });
      fetchDrafts();
    } catch (err) {
      console.error('Delete draft error:', err);
    }
  };

  const startFromDraft = (draft) => {
    if (onStartOrder) onStartOrder(draft);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.headerTitle}>Draft Order Queue</span>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={fetchDrafts} style={styles.refreshBtn} title="Refresh">↻</button>
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
        </div>

        <div style={styles.body}>
          {loading && <div style={styles.emptyState}>Loading drafts…</div>}

          {!loading && drafts.length === 0 && (
            <div style={styles.emptyState}>
              No pending draft orders. Stylists can create them via the AI Assistant.
            </div>
          )}

          {!loading && drafts.map(draft => {
            const pj = draft.parsedJson || {};
            const isExpanded = expanded === draft.id;
            const isClaimed = draft.status === 'claimed';

            return (
              <div key={draft.id} style={{ ...styles.card, ...(isClaimed ? styles.cardClaimed : {}) }}>
                {/* Card header row */}
                <div style={styles.cardHeader}>
                  <div>
                    <span style={styles.customerName}>
                      {pj.customerName || 'Unknown Customer'}
                    </span>
                    {pj.orderType && (
                      <span style={styles.orderTypeBadge}>{pj.orderType}</span>
                    )}
                    {pj.stylistName && (
                      <span style={styles.stylistBadge}>✂️ {pj.stylistName}</span>
                    )}
                    {isClaimed && (
                      <span style={styles.claimedBadge}>Claimed by {draft.claimedBy}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={styles.timestamp}>{formatAge(draft.dtCreated)}</span>
                    <button
                      onClick={() => setExpanded(isExpanded ? null : draft.id)}
                      style={styles.expandBtn}
                    >
                      {isExpanded ? '▲' : '▼'}
                    </button>
                  </div>
                </div>

                {/* Quick info line */}
                <div style={styles.cardMeta}>
                  {pj.barcode && <span>🏷 {pj.barcode}</span>}
                  {pj.pickupDate && <span>📅 {pj.pickupDate}</span>}
                  {Array.isArray(pj.rooms) && pj.rooms.length > 0 && (
                    <span>🏠 {pj.rooms.map(r => r.roomName).join(', ')}</span>
                  )}
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div style={styles.details}>
                    {draft.rawTranscript && (
                      <div style={styles.transcriptBox}>
                        <strong>Transcript:</strong>
                        <p style={{ margin: '4px 0 0', color: '#555' }}>{draft.rawTranscript}</p>
                      </div>
                    )}
                    {Array.isArray(pj.rooms) && pj.rooms.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <strong>Room Instructions:</strong>
                        {pj.rooms.map((r, i) => (
                          <div key={i} style={styles.roomRow}>
                            <span style={styles.roomLabel}>{r.roomName}</span>
                            <span style={styles.roomNotes}>{r.taskNotes || '—'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div style={styles.cardActions}>
                  <button onClick={() => deleteDraft(draft.id)} style={styles.deleteBtn} title="Remove from queue">
                    🗑 Delete
                  </button>
                  <button onClick={() => startFromDraft(draft)} style={styles.startBtn}>
                    Start Order from Draft
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function formatAge(dtStr) {
  if (!dtStr) return '';
  const diff = Date.now() - new Date(dtStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9998,
  },
  panel: {
    background: '#fff', borderRadius: 12,
    width: '100%', maxWidth: 620,
    maxHeight: '88vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px',
    background: '#1a1a2e', color: '#fff',
  },
  headerTitle: { fontWeight: 700, fontSize: 16 },
  refreshBtn: {
    background: 'none', border: '1px solid rgba(255,255,255,0.3)',
    color: '#fff', borderRadius: 6,
    padding: '3px 8px', fontSize: 16, cursor: 'pointer',
  },
  closeBtn: {
    background: 'none', border: 'none', color: '#fff',
    fontSize: 18, cursor: 'pointer',
  },
  body: {
    flex: 1, overflowY: 'auto',
    padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
  },
  emptyState: {
    textAlign: 'center', color: '#888',
    padding: '40px 20px', fontSize: 15,
  },
  card: {
    border: '1px solid #e5e7eb', borderRadius: 10,
    padding: '12px 14px',
    background: '#fafafa',
  },
  cardClaimed: { background: '#f9fafb', opacity: 0.8 },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 6,
  },
  customerName: { fontWeight: 700, fontSize: 15, marginRight: 8 , color: '#111' },
  orderTypeBadge: {
    background: '#e0e7ff', color: '#3730a3',
    borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 600,
    marginRight: 6,
  },
  stylistBadge: {
    background: '#fce7f3', color: '#9d174d',
    borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 600,
    marginRight: 6,
  },
  claimedBadge: {
    background: '#fef9c3', color: '#854d0e',
    borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 600,
  },
  timestamp: { color: '#9ca3af', fontSize: 12, whiteSpace: 'nowrap' },
  expandBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 12, color: '#6b7280', padding: '2px 6px',
  },
  cardMeta: {
    display: 'flex', gap: 14, flexWrap: 'wrap',
    fontSize: 13, color: '#555', marginBottom: 8,
  },
  details: {
    borderTop: '1px solid #e5e7eb',
    paddingTop: 10, marginTop: 6,
  },
  transcriptBox: {
    background: '#f3f4f6', borderRadius: 8,
    padding: '8px 12px', fontSize: 13,
  },
  roomRow: {
    display: 'flex', gap: 10, alignItems: 'flex-start',
    marginTop: 6,
  },
  roomLabel: {
    background: '#dcfce7', color: '#166534',
    borderRadius: 20, padding: '2px 10px',
    fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  roomNotes: { fontSize: 13, color: '#374151', lineHeight: 1.4 },
  cardActions: {
    display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end',
  },
  claimBtn: {
    background: '#fff', border: '1px solid #6366f1', color: '#6366f1',
    borderRadius: 7, padding: '6px 14px', fontSize: 13,
    fontWeight: 600, cursor: 'pointer',
  },
  deleteBtn: {
    background: '#fff', border: '1px solid #dc2626', color: '#dc2626',
    borderRadius: 7, padding: '6px 14px', fontSize: 13,
    fontWeight: 600, cursor: 'pointer',
  },
  startBtn: {
    background: '#1a1a2e', color: '#fff',
    border: 'none', borderRadius: 7, padding: '6px 14px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
};
